import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ScreenSizeDetectorService } from '@serviceplan/ui-kit';
import {
  AssetTagsService,
  AssetUIModel,
  AssetsGenerateUrlService,
  ClientEnvironment,
  CommonUtilityLib,
  ENVIRONMENT,
  StorageUtilityLib,
  UserSession,
  ValueListsService,
  anyAsset,
} from '@thehubz/client/core';
import { Asset, Collection, CollectionsApiService, Tags, UserInfo, UserValue } from '@thehubz/client/data-access-api';
import { CustomClients } from '@thehubz/shared/branding';
import { AnyType, legacyId, legacyIdX } from '@thehubz/shared/model';
import { Subscription, combineLatest, filter, take, tap } from 'rxjs';
import { AssetCommonService } from '../../../../services/asset-common.service';
import { CollectionEditStepperService } from '../../../../services/collection-edit-stepper.service';
import { CollectionEventsService } from '../../services/collection-events.service';

@Component({
  selector: 'thehubz-general-info',
  templateUrl: './general-info.component.html',
  styleUrls: ['./general-info.component.scss'],
})
export class GeneralInfoComponent implements OnInit, OnDestroy {
  selectedCovered?: AssetUIModel;
  collectionInfo!: Collection;
  // collection created by
  collectionCreatedBy!: string;
  // collection owners with same ACB
  collectionOwners!: string[];
  // owners with same ACB
  owners!: UserInfo[];
  assetsDetailList!: AssetUIModel[];
  collectionForm: FormGroup = new FormGroup([]);
  keywords: string[] = [];
  taggedUser!: UserInfo[]; // tagged user list
  descriptionText!: string; // sp-editor description text
  /** Auto complete tag data */
  autoCompTags: Tags[] = [];
  /** Store all tags associated with asset */
  tagsInfo: Tags[] = [];

  AnyAsset = anyAsset;
  currentUser!: UserSession;
  isOwner = false;

  usersLikeMe!: UserValue[];

  subscriptions?: Subscription;
  tagSub?: Subscription;

  readonly brandingKey: CustomClients;
  CustomClients = CustomClients;

  chipRequiredText = $localize`Tags are required`;
  chipToolTipText = $localize`Press \'Enter\' or \'Comma\' to add tags`;
  ownersPlaceholder = $localize`Select owner`;
  ownersSearchPlaceholder = $localize`Search owner...`;

  constructor(
    @Inject(ENVIRONMENT) protected readonly env: ClientEnvironment,
    private fb: FormBuilder,
    private assetTagsService: AssetTagsService,
    private commonUtilityLib: CommonUtilityLib,
    protected storageUtility: StorageUtilityLib,
    protected valueListsService: ValueListsService,
    protected assetCommonService: AssetCommonService,
    private assetUrlService: AssetsGenerateUrlService,
    protected collectionAPIService: CollectionsApiService,
    private collectionEventService: CollectionEventsService,
    protected screenSizeDetectorService: ScreenSizeDetectorService,
    private collectionEditStepperService: CollectionEditStepperService,
  ) {
    this.brandingKey = this.env.brandingKey;
  }

  ngOnInit(): void {
    // get collection asset observable if collection asset has some length
    const collectionAssetDataSubject$ = this.collectionEventService.collectionAssetDataSubject$.pipe(filter((collAsset) => collAsset.length > 0));
    // get collection data observable if collection object is not empty
    const collectionDataSubject$ = this.collectionEventService.collectionDataSubject$.pipe(filter((coll) => !this.commonUtilityLib.isEmptyObject(coll)));
    // get list of users with same ACB
    const usersLikeMe$ = this.valueListsService.listUsersLikeMe(['global.library']);
    // combine all 3 observable
    const subs = combineLatest([collectionAssetDataSubject$, collectionDataSubject$, usersLikeMe$])
      .pipe(take(1))
      .subscribe(async (res) => {
        // TODO: remove banner check if fallback image issue fixed
        this.assetsDetailList = res[0].filter((asset) => asset.imageUrl && asset.assetType !== Asset.AssetTypeEnum.HtmlBanner);
        // assign value to collectionInfo
        this.collectionInfo = res[1];
        this.collectionCreatedBy = legacyIdX(this.collectionInfo.createdBy);
        this.collectionOwners = this.collectionInfo.ownerIDs || [];
        // get unique owners
        this.collectionOwners = [...new Set(this.collectionOwners)];
        this.usersLikeMe = res[2];

        // assign value to owners
        this.owners = res[2].map((user) => ({ id: user.id, name: user.name, email: user.email }));
        const coverAsset = this.collectionInfo.coverAssetID;
        if (coverAsset && typeof coverAsset === 'object') {
          this.setSelectedCover(coverAsset as AssetUIModel);
        }

        const currentOwners = [...(this.collectionInfo.ownerIDs || [])];
        currentOwners.push(this.collectionCreatedBy);
        const currentUserId = this.assetCommonService.currentUser.userData._id;
        this.isOwner = this._isUserOwner(currentUserId, currentOwners);
        await this.addKeywords();
      });

    // form group
    this.collectionForm = this.fb.group({
      name: ['', Validators.compose([Validators.required])],
      description: ['', Validators.compose([Validators.required])],
      keywords: ['', Validators.compose([Validators.required])],
      coverAssetID: [''],
      ownerIDs: [''],
    });

    this.subscriptions = subs;
    this.tagSub = this.assetTagsService
      .fetchTagsForAutoComp(this.collectionForm.get('keywords'))
      ?.pipe(tap((data) => (this.autoCompTags = data)))
      .subscribe();
  }

  private _isUserOwner(currentUserId: string, currentOwners: string[]): boolean {
    return currentOwners.some((owner) => owner === currentUserId);
  }

  /**
   * store tagged user list when user tagged get at sp-editor
   * @param userList
   */
  taggedUserList(userList: UserInfo[]) {
    this.taggedUser = userList;
  }

  /**
   * store description text from sp-editor change
   * @param descriptionText
   */
  editorDescriptionText(descriptionText: string) {
    this.descriptionText = descriptionText;
  }

  /**
   * edit mode
   */
  editInformation() {
    this.collectionEditStepperService.collectionEditStepper(this.collectionInfo).then((result: AnyType) => {
      if (result) {
        this.collectionInfo = result;
        this.collectionEventService.collectionDataSubject$.next(this.collectionInfo);
        this.commonUtilityLib.emitNewHeaderline(this.collectionInfo.name);
        this.addKeywords();
        const coverAsset = result.coverAssetObj;
        if (coverAsset && typeof coverAsset === 'object') {
          this.setSelectedCover(coverAsset as AssetUIModel);
        }
        this.collectionOwners = result.ownerIDs || [];
      }
    });
  }

  /* add keywords with page loads */
  async addKeywords() {
    if (this.collectionInfo?.keywords?.length) {
      this.tagsInfo = await this.valueListsService.listTags(this.collectionInfo.keywords);
      // Map the sorted array to get tag names
      this.keywords = this.tagsInfo.map((tag) => tag.name);
      this.assetTagsService.updateTagsArray(this.keywords);
    }
    this.patchForm();
  }

  /**
   * patch form
   */
  patchForm() {
    this.collectionForm.patchValue({
      name: this.collectionInfo.name,
      description: this.collectionInfo.description,
      keywords: this.tagsInfo.length ? this.tagsInfo.map((tag: AnyType) => tag.name) : [],
      coverAssetID: this.collectionInfo.coverAssetID,
      ownerIDs: this.collectionOwners.filter((owner) => owner !== legacyId(this.collectionCreatedBy)),
    });
  }

  /**
   * set the selected cover from the asset list
   * @param coverID
   */
  setSelectedCover(coverAsset: AssetUIModel) {
    const coverAssetWithPreview = this.assetUrlService.addPreviewUrl(coverAsset); console.log('🚀 ~ GeneralInfoComponent ~ setSelectedCover ~ coverAsset:', coverAsset);
    this.selectedCovered = coverAssetWithPreview;
  }

  /**
   * Add keywords
   * @param event
   * @returns
   */
  add(event: string): void {
    if (event) {
      if (this.keywords.includes(event)) {
        return;
      }
      this.keywords.push(event);
      this.keywords.sort((a, b) => a.localeCompare(b));
      this.assetTagsService.updateTagsArray(this.keywords);
      this.collectionForm.controls['keywords'].patchValue(this.keywords);
    }
  }

  /**
   * remove keyword
   * @param tag
   */
  removeKeyword(key: string): void {
    const index = this.keywords.indexOf(key);
    if (index >= 0) {
      this.keywords.splice(index, 1);
      this.tagsInfo.splice(index, 1);
      this.keywords.sort((a, b) => a.localeCompare(b));
      this.collectionForm.controls['keywords'].patchValue(this.keywords);
    }
  }
  /**
   * Remove chip from multi select
   * @param value
   */
  removeUserChip(owner: AnyType) {
    const owners = this.collectionForm.get('ownerIDs')?.value;
    const modifiedOwners = this.commonUtilityLib.removeObjectFromArray(owners, owner.list.id);
    this.collectionForm.get('ownerIDs')?.setValue(modifiedOwners);
    this.collectionOwners = modifiedOwners;
  }

  ngOnDestroy(): void {
    if (this.subscriptions) {
      this.subscriptions.unsubscribe();
    }
  }
}
